/**
 * Query-key factory for the PDF Setup view.
 *
 * Kept feature-scoped (inside `Publications/SetupPDF/hooks/`) because this
 * is the second slice of the compose-app → TanStack Query migration and the
 * keys are only used locally. A future slice can promote this into a
 * shared module if other features need to invalidate the same keys.
 *
 * Shape: `['publicationSetupPdf', 'styles', { exportType, exportId }]`
 *
 * The key carries the same `exportType` / `exportId` pair the server uses
 * to identify a styles row. Callers pick the mode — section route always
 * passes `'section'`, publication route always passes `'publication'`.
 * A broad invalidation `[...all, 'styles']` still matches both variants.
 */

export type PublicationSetupPdfStylesKeyParams = {
  exportType: 'publication' | 'section';
  exportId: string;
};

export const publicationSetupPdfQueryKeys = {
  all: ['publicationSetupPdf'] as const,

  /** Saved PDF styles for either a publication or a section. */
  styles: (params: PublicationSetupPdfStylesKeyParams) =>
    [...publicationSetupPdfQueryKeys.all, 'styles', params] as const,

  /**
   * Stories hierarchy (sections + stories) for the sidebar panel.
   * `selectedStoryTypes` is part of the key — different filters produce
   * different results and each needs its own cache entry.
   */
  hierarchy: (params: PublicationSetupPdfStylesKeyParams & { selectedStoryTypes: string[] }) =>
    [...publicationSetupPdfQueryKeys.all, 'hierarchy', params] as const,

  /**
   * Full HTML content for a single story (story details + merge tags applied
   * + HTML generation). Keyed by storyId — previous stories stay cached so
   * going back is instant. `progressTrackerSettings` is NOT in the key
   * intentionally: the original effect only re-ran on storyId change, and
   * we preserve that behaviour here.
   */
  storyHtml: (storyId: string) =>
    [...publicationSetupPdfQueryKeys.all, 'storyHtml', storyId] as const,

  /** Mutation key for upsert (insert_exportPdfSettings_one). */
  upsertStyles: (params: PublicationSetupPdfStylesKeyParams) =>
    [...publicationSetupPdfQueryKeys.all, 'upsertStyles', params] as const,

  /** Mutation key for delete (delete_exportPdfSettings). */
  deleteStyles: (params: PublicationSetupPdfStylesKeyParams) =>
    [...publicationSetupPdfQueryKeys.all, 'deleteStyles', params] as const
} as const;
